'use client';

import { c as _c } from "react/compiler-runtime";
import type { WikiContentDisplayProps } from '@/types/wiki-content';
import { useEffect, useMemo, useRef } from 'react';
import { MixedMarkdownMermaidRenderer } from '@/components/markdown-renderer/mixed-markdown-mermaid-renderer';
import { removeMarkdownTitleHeadings } from '@/components/wiki/markdown-title-headings';
import { WikiLayout } from '@/components/wiki/wiki-layout';
import { WikiRepoLabel, WikiSidebarHeader } from '@/components/wiki/wiki-sidebar-header';
import { WikiLoadingState } from '@/components/wiki/wiki-states';
import { WikiGenerationStatus } from '@/generated/prisma/enums';
import { decodeHtmlEntities } from '@/lib/html-decode';
import { cn } from '@/lib/utils';

// Wiki body uses prose-invert (~#d1d5dc), not text-foreground (~#fafafa). Use muted-foreground
// for a visible step down from default prose body while keeping headings bright white.
const WIKI_BODY_PROSE_CLASSNAME = '[--tw-prose-body:hsl(var(--muted-foreground))] [--tw-prose-bold:hsl(var(--muted-foreground))] prose-p:text-muted-foreground prose-li:text-muted-foreground prose-ul:text-muted-foreground prose-ol:text-muted-foreground prose-strong:text-muted-foreground';
export function WikiContentDisplay(t0) {
  const $ = _c(54);
  const {
    pages,
    wikiStructure,
    wikiData,
    selectedPageRef,
    setSelectedPageRef,
    owner,
    repoName,
    repositoryId,
    installationId,
    onRefresh,
    isRefreshing,
    isLastRefreshFailed: t1,
    selectedVersion,
    wikiVersions,
    showContent: t2,
    isLoading: t3,
    noContentUI,
    isPublicView: t4,
    isPublicRepo: t5,
    afterContent
  } = t0;
  const isLastRefreshFailed = t1 === undefined ? false : t1;
  const showContent = t2 === undefined ? true : t2;
  const isLoading = t3 === undefined ? false : t3;
  const isPublicView = t4 === undefined ? false : t4;
  const isPublicRepo = t5 === undefined ? false : t5;
  const contentRef = useRef(null);
  let t6;
  if ($[0] !== pages || $[1] !== selectedPageRef) {
    t6 = pages.find(p => p.pageRef === selectedPageRef) ?? pages[0];
    $[0] = pages;
    $[1] = selectedPageRef;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const current = t6;
  const currentContent = current?.contentMd ?? current?.content ?? "";
  let t7;
  if ($[3] !== currentContent) {
    t7 = removeMarkdownTitleHeadings(currentContent);
    $[3] = currentContent;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const renderedContent = t7;
  let t8;
  if ($[5] !== pages || $[6] !== wikiData?.generation_status) {
    t8 = wikiData?.generation_status === WikiGenerationStatus.completed && pages.some(_temp);
    $[5] = pages;
    $[6] = wikiData?.generation_status;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const hasExportableContent = t8;
  const wikiLanguage = wikiData?.language ?? null;
  let t9;
  if ($[8] !== selectedPageRef) {
    t9 = [selectedPageRef];
    $[8] = selectedPageRef;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  useEffect(_temp2, t9);
  const currentOwner = owner || "";
  const currentRepoName = repoName || "";
  const hasContent = showContent && (current || pages.length > 0);
  const shouldRenderNoContentUI = noContentUI && (isLoading || !hasContent);
  const shouldSuppressSidebarLoading = noContentUI && !isLoading && !hasContent;
  let t10;
  if ($[10] !== shouldSuppressSidebarLoading || $[11] !== wikiVersions) {
    t10 = shouldSuppressSidebarLoading && wikiVersions === undefined ? [] : wikiVersions;
    $[10] = shouldSuppressSidebarLoading;
    $[11] = wikiVersions;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const resolvedWikiVersions = t10;
  let t11;
  bb0: {
    if (!currentOwner || !currentRepoName) {
      t11 = null;
      break bb0;
    }
    if (isPublicView) {
      let t12;
      if ($[13] !== currentOwner || $[14] !== currentRepoName) {
        t12 = <WikiRepoLabel owner={currentOwner} repo={currentRepoName} />;
        $[13] = currentOwner;
        $[14] = currentRepoName;
        $[15] = t12;
      } else {
        t12 = $[15];
      }
      t11 = t12;
      break bb0;
    }
    const t12 = installationId ?? null;
    const t13 = repositoryId ?? null;
    const t14 = wikiData?.created_at ?? undefined;
    const t15 = pages.length > 0 ? onRefresh : undefined;
    let t16;
    if ($[16] !== currentOwner || $[17] !== currentRepoName || $[18] !== hasExportableContent || $[19] !== isLastRefreshFailed || $[20] !== isLoading || $[21] !== isPublicRepo || $[22] !== isRefreshing || $[23] !== resolvedWikiVersions || $[24] !== selectedVersion || $[25] !== t12 || $[26] !== t13 || $[27] !== t14 || $[28] !== t15 || $[29] !== wikiLanguage) {
      t16 = <WikiSidebarHeader owner={currentOwner} repo={currentRepoName} installationId={t12} repositoryId={t13} lastUpdatedAt={t14} isWikiLoading={isLoading} isRefreshing={isRefreshing} isLastRefreshFailed={isLastRefreshFailed} onRefresh={t15} selectedVersion={selectedVersion} wikiVersions={resolvedWikiVersions} hasExportableContent={hasExportableContent} wikiLanguage={wikiLanguage} isPublicRepo={isPublicRepo} />;
      $[16] = currentOwner;
      $[17] = currentRepoName;
      $[18] = hasExportableContent;
      $[19] = isLastRefreshFailed;
      $[20] = isLoading;
      $[21] = isPublicRepo;
      $[22] = isRefreshing;
      $[23] = resolvedWikiVersions;
      $[24] = selectedVersion;
      $[25] = t12;
      $[26] = t13;
      $[27] = t14;
      $[28] = t15;
      $[29] = wikiLanguage;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    t11 = t16;
  }
  const sidebarHeader = t11;
  let t12;
  if ($[31] !== afterContent || $[32] !== current || $[33] !== currentOwner || $[34] !== currentRepoName || $[35] !== hasContent || $[36] !== isPublicView || $[37] !== noContentUI || $[38] !== renderedContent || $[39] !== shouldRenderNoContentUI || $[40] !== wikiData?.source_branch || $[41] !== wikiData?.source_commit_sha) {
    t12 = shouldRenderNoContentUI ? noContentUI : hasContent ? <><div className="mb-8"><h1 className="text-4xl font-bold mb-4">{current?.title ? decodeHtmlEntities(current.title) : ""}</h1></div><div ref={contentRef} id="wiki-content" className={cn("prose prose-sm dark:prose-invert max-w-none custom-scrollbar overflow-visible", "prose-headings:scroll-mt-32 prose-headings:font-semibold prose-h1:hidden", "prose-h2:text-xl prose-h2:mt-16 prose-h2:mb-6", "prose-h3:text-lg prose-h3:mt-12 prose-h3:mb-4", "prose-h4:text-base prose-h4:mt-8 prose-h4:mb-3", "prose-h5:text-sm prose-h5:mt-6 prose-h5:mb-2", "[&_h2+h3]:mt-6 [&_h3+h4]:mt-4 [&_h4+h5]:mt-3", "prose-p:leading-7 prose-p:mb-4", "prose-strong:font-semibold", "[&_p:has(>em:only-child)]:text-xs [&_p:has(>em:only-child)]:text-muted-foreground [&_p:has(>em:only-child)]:mt-2 [&_p:has(>em:only-child)]:mb-6", "[&_p:has(>strong:first-child)]:text-xs [&_p:has(>strong:first-child)]:text-muted-foreground [&_p:has(>strong:first-child)]:mt-4", "[&_p>strong:first-child]:text-xs", "prose-code:text-sm prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded", "prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:my-2", "prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline [&_a:hover]:underline", "prose-img:rounded-lg prose-img:border prose-img:border-border", "prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground", "prose-ul:my-2 prose-ol:my-2", "prose-li:marker:text-muted-foreground/50", "prose-table:w-full prose-table:my-6 prose-thead:bg-muted/30", "prose-th:p-2 prose-th:text-sm prose-th:font-medium prose-td:p-2", "[&_tbody_tr:hover]:bg-muted/10 [&_tbody_tr]:transition-colors", "[&_table_th:first-child]:pl-4 [&_table_td:first-child]:pl-4", "[&_table_td>code]:text-xs [&_table_td>code]:px-1.5 [&_table_td>code]:py-0.5 [&_table_td>code]:bg-muted [&_table_td>code]:rounded", "[&>pre]:my-2 [&>ul]:my-2 [&>ol]:my-2", "[&>h2+pre]:mt-2 [&>h3+pre]:mt-2 [&>h4+pre]:mt-2", "[&>h2+ul]:mt-2 [&>h3+ul]:mt-2 [&>h4+ul]:mt-2")}><MixedMarkdownMermaidRenderer content={renderedContent} org={currentOwner} repo={currentRepoName} isPublicView={isPublicView} sourceCommitSha={wikiData?.source_commit_sha} sourceBranch={wikiData?.source_branch} className={WIKI_BODY_PROSE_CLASSNAME} /></div>{afterContent}</> : <WikiLoadingState />;
    $[31] = afterContent;
    $[32] = current;
    $[33] = currentOwner;
    $[34] = currentRepoName;
    $[35] = hasContent;
    $[36] = isPublicView;
    $[37] = noContentUI;
    $[38] = renderedContent;
    $[39] = shouldRenderNoContentUI;
    $[40] = wikiData?.source_branch;
    $[41] = wikiData?.source_commit_sha;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== currentContent || $[44] !== currentOwner || $[45] !== currentRepoName || $[46] !== isPublicView || $[47] !== pages || $[48] !== selectedPageRef || $[49] !== setSelectedPageRef || $[50] !== sidebarHeader || $[51] !== t12 || $[52] !== wikiStructure) {
    t13 = <WikiLayout pages={pages} selectedPageRef={selectedPageRef} onPageSelect={setSelectedPageRef} wikiStructure={wikiStructure} sidebarHeader={sidebarHeader} content={currentContent} isPublicView={isPublicView} owner={currentOwner} repo={currentRepoName}>{t12}</WikiLayout>;
    $[43] = currentContent;
    $[44] = currentOwner;
    $[45] = currentRepoName;
    $[46] = isPublicView;
    $[47] = pages;
    $[48] = selectedPageRef;
    $[49] = setSelectedPageRef;
    $[50] = sidebarHeader;
    $[51] = t12;
    $[52] = wikiStructure;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  return t13;
}
function _temp2() {
  const mainContent = document.getElementById("wiki-main-content");
  if (mainContent) {
    mainContent.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }
}
function _temp(page) {
  return (page.contentMd ?? page.content ?? "").trim().length > 0;
}